import React, { useEffect, useRef, useState } from 'react';

import Loading from '../Loading';
import InnaAvatar from './InnaAvatar';
import MessageBody from './MessageBody';
import { SUGGESTIONS } from '../../hooks/useChat';

// Shown only until the agent says what it is actually doing. Once a tool runs
// the status comes from the stream, and once text arrives the answer speaks for
// itself - so this is the first second or two, not the whole wait.
const WAITING = [
    'Asking BigBobby nicely…',
    'Counting audiobooks…',
    'Rummaging through the shelves…',
    'Digging through the catalogue…',
    'Waking up BigBobby…',
    'Consulting BigBobby, who knows all…',
    'Bribing BigBobby with coffee…',
    'Poking BigBobby awake…',
    'Letting BigBobby cook…',
    'Summoning BigBobby from the depths…',
    'BigBobby says "one sec"…',
    'BigBobby is flexing his omniscience…',
    'BigBobby is scanning the multiverse of data…'
];

const ChatTranscript = ({ messages, loading, stream, onSuggestion, compact = false }) => {
    const transcriptRef = useRef(null);
    const [waiting, setWaiting] = useState(WAITING[0]);

    const streamingText = stream ? stream.text : '';
    const status = stream ? stream.status : null;

    useEffect(() => {
        // Keep the newest turn in view as answers arrive.
        const node = transcriptRef.current;
        if (node) {
            node.scrollTop = node.scrollHeight;
        }
    }, [messages, loading, streamingText, status]);

    // Chosen when the wait starts and held for its duration - text that changed
    // underneath a running spinner would read as progress it is not making. The
    // stream's own status lines do change, but each one marks a real step.
    useEffect(() => {
        if (loading) {
            setWaiting(WAITING[Math.floor(Math.random() * WAITING.length)]);
        }
    }, [loading]);

    return (
        <div className="chat-transcript" ref={transcriptRef}>
            {!messages.length && (
                <div className="chat-empty">
                    <div className="chat-intro">
                        <InnaAvatar
                            size={compact ? 56 : 160}
                            variant={compact ? 'avatar' : 'shoes'}
                            className={compact ? undefined : 'chat-intro-illustration'}
                        />
                        <p className="chat-intro-text">
                            Hi, I&apos;m <strong>INNA</strong>. Ask me about the catalogue, sales, availability,
                            licensors or audio operations — I use BigBobby data for quick research and discovery.
                        </p>
                    </div>
                    <ul className="chat-suggestions">
                        {SUGGESTIONS.map((suggestion) => (
                            <li key={suggestion}>
                                <button
                                    type="button"
                                    className="chat-suggestion"
                                    onClick={() => onSuggestion(suggestion)}
                                >
                                    {suggestion}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {messages.map((message, index) => (
                <div
                    key={index}
                    className={`chat-message chat-message-${message.role}${
                        message.isError ? ' chat-message-error' : ''
                    }${compact ? ' chat-message-compact' : ''}`}
                >
                    {message.role === 'assistant' && <InnaAvatar size={28} className="chat-message-avatar" />}
                    <div className="chat-bubble">
                        <MessageBody text={message.body} />
                    </div>
                </div>
            ))}

            {loading && (
                <div
                    className={`chat-message chat-message-assistant${
                        compact ? ' chat-message-compact' : ''
                    }`}
                >
                    <InnaAvatar size={28} className="chat-message-avatar" />
                    {streamingText ? (
                        // Once text is arriving it is the answer, so it renders
                        // as one - the caret is the only thing marking it as
                        // still being written.
                        <div className="chat-bubble chat-bubble-streaming">
                            <MessageBody text={streamingText} />
                            <span className="chat-caret" aria-hidden="true" />
                        </div>
                    ) : (
                        <div className="chat-bubble chat-bubble-pending">
                            <Loading inline />
                            <span className="ml-2">{status || waiting}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Tool progress under a part-written answer: the agent often
                narrates, queries, then continues, and a silent 20s gap
                mid-sentence otherwise looks like a stall. */}
            {loading && streamingText && status && (
                <div className="chat-message chat-message-assistant chat-message-status">
                    <span className="chat-status">
                        <Loading inline />
                        <span className="ml-2">{status}</span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default ChatTranscript;
